import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import type { SellerDashboard } from '@chicare/shared';

import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { Icon, type IconName } from '@/components/icons/Icon';
import { Skeleton } from '@/components/ui/Skeleton';
import { AreaChart, BarList, Gauge } from '@/components/charts/Chart';
import { AdminError } from '@/features/admin/AdminError';

const RANGES = [
  [7, '7 days'],
  [30, '30 days'],
  [90, '90 days'],
] as const;

/**
 * What this pharmacy has actually been doing.
 *
 * ── WHY A SELLER NEEDS THIS AND NOT JUST A QUEUE ────────────────────────────
 *
 * The console could answer "what do I do next" and nothing else. A pharmacy
 * deciding whether ChiCare was worth the counter space had no way to tell, and
 * — worse — a pharmacy quietly losing orders to a missed accept window had no
 * way to notice. Nothing on their screen went red; the orders simply stopped
 * arriving.
 *
 * So the first number on this page is money, and the second is the one they
 * control completely.
 *
 * ── AND WHY "INCOME" AND NOT "MONEY" ────────────────────────────────────────
 *
 * The old tab was called Money, which is what the section is about rather than
 * what it tells you. Income is the number a pharmacist came to look at.
 */
export function SellerDashboardScreen() {
  const [days, setDays] = useState<number>(30);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['seller', 'dashboard', days],
    queryFn: ({ signal }) => api.sellerDashboard(days, signal),
    staleTime: 60_000,
  });

  if (isError) {
    return (
      <AdminError title="We couldn't load your dashboard" error={error} onRetry={() => refetch()} />
    );
  }

  return (
    <div className="mx-auto w-full max-w-[1100px] px-4 py-6 xl:px-8">
      <header className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="m-0 font-display text-[26px] font-bold tracking-[-0.02em]">Dashboard</h1>
          <p className="m-0 mt-1 text-[13px] text-muted">
            Your shop at a glance — what is waiting, what you earned, what is working.
          </p>
        </div>

        <RangePicker days={days} onChange={setDays} />
      </header>

      {isLoading || !data ? <Loading /> : <Body data={data} />}
    </div>
  );
}

function RangePicker({ days, onChange }: { days: number; onChange: (days: number) => void }) {
  return (
    <div role="group" aria-label="How far back" className="flex gap-1 rounded-pill bg-paper p-1">
      {RANGES.map(([value, label]) => (
        <button
          key={value}
          type="button"
          onClick={() => onChange(value)}
          aria-pressed={days === value}
          className={cn(
            'min-h-9 rounded-pill px-3 text-[12.5px] font-bold',
            days === value ? 'bg-white text-ink shadow-sm' : 'bg-transparent text-muted',
          )}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

function Body({ data }: { data: SellerDashboard }) {
  return (
    <>
      {/* Anything that needs doing today, before any of the history. A trend
          is interesting; an order about to time out is urgent. */}
      <Pipeline pipeline={data.pipeline} />
      <Attention data={data} />

      <section className="mb-4 rounded-card border border-line bg-white p-4 sm:p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="m-0 font-mono text-[10px] uppercase tracking-[0.12em] text-faint">
              You earned
            </p>
            <p className="m-0 mt-1 font-display text-[30px] font-bold leading-none tracking-[-0.02em] tabular">
              {data.income.earned_display}
            </p>
            <Change bps={data.income.change_bps} was={data.income.previous_display} />
          </div>

          {data.income.awaiting_settlement_kobo > 0 ? (
            <div className="rounded-card border border-line bg-paper px-3.5 py-2.5 text-right">
              <p className="m-0 font-mono text-[10px] uppercase tracking-[0.12em] text-faint">
                On its way
              </p>
              <p className="m-0 mt-0.5 font-mono text-[15px] font-bold tabular text-ink">
                {data.income.awaiting_settlement_display}
              </p>
              {/* Said plainly, because its absence is the commonest support
                  call a marketplace gets. */}
              <p className="m-0 mt-0.5 text-[11px] text-faint">Captured, not yet settled</p>
            </div>
          ) : null}
        </div>

        <AreaChart
          className="mt-4"
          points={data.income.series}
          height={148}
          format={(value) => nairaish(value)}
        />
      </section>

      <div className="mb-4 grid gap-3 md:grid-cols-2">
        <section className="rounded-card border border-line bg-white p-4">
          <p className="m-0 font-mono text-[10px] uppercase tracking-[0.12em] text-faint">Orders</p>
          <p className="m-0 mt-1 font-display text-[24px] font-bold leading-none tracking-[-0.02em] tabular">
            {data.orders.count}
          </p>
          <Change bps={data.orders.change_bps} was={`${data.orders.previous}`} />
          <AreaChart className="mt-3" points={data.orders.series} height={92} />
        </section>

        {/* The two rates a seller can actually move. Gauges rather than bars,
            because 94% and 97% are two bars nobody can tell apart. */}
        <section className="rounded-card border border-line bg-white p-4">
          <p className="m-0 mb-3 font-mono text-[10px] uppercase tracking-[0.12em] text-faint">
            How you are doing
          </p>

          <div className="flex flex-wrap items-start justify-around gap-4">
            <Gauge bps={data.fulfilment.accept_rate_bps} label="Orders accepted" />
            <Gauge bps={data.prescriptions.reply_rate_bps} label="Prescriptions answered" />
          </div>

          <dl className="mt-4 grid grid-cols-2 gap-x-3 gap-y-2 border-t border-line pt-3 text-[12px]">
            <Fact label="Delivered" value={`${data.fulfilment.delivered}`} />
            <Fact
              label="Average accept"
              value={
                data.fulfilment.average_accept_minutes === null
                  ? '—'
                  : `${data.fulfilment.average_accept_minutes} min`
              }
            />
            {/* The number that costs the most and shows up nowhere else. */}
            <Fact
              label="Timed out"
              value={`${data.fulfilment.missed}`}
              bad={data.fulfilment.missed > 0}
            />
            <Fact label="Prescriptions asked" value={`${data.prescriptions.asked}`} />
          </dl>
        </section>
      </div>

      <div className="mb-4 grid gap-3 md:grid-cols-2">
        <StockCard stock={data.stock} />
        <PayoutsCard payouts={data.payouts} customers={data.customers} />
      </div>

      <div className="mb-4 grid gap-3 md:grid-cols-2">
        <RhythmCard busiest={data.busiest} />
        <ActivityCard activity={data.activity} />
      </div>

      <section className="rounded-card border border-line bg-white p-4 sm:p-5">
        <p className="m-0 mb-3 font-mono text-[10px] uppercase tracking-[0.12em] text-faint">
          What earned most
        </p>

        {data.top_products.length === 0 ? (
          <p className="m-0 text-[13px] text-muted">
            Nothing has sold in this period yet. Once it does, the products that earn most appear
            here.
          </p>
        ) : (
          <BarList
            rows={data.top_products.map((product) => ({
              label: product.name,
              value: product.revenue_kobo,
              display: product.revenue_display,
              hint: `${product.units} sold`,
            }))}
          />
        )}
      </section>
    </>
  );
}

/**
 * The counter, right now.
 *
 * Four states an order can be sitting in, and the one at the front is on a
 * fifteen-minute clock. This is the part of the page that makes it worth
 * opening every morning rather than once a week — everything below it is
 * history, and history does not need a pharmacist to do anything.
 */
function Pipeline({ pipeline }: { pipeline: SellerDashboard['pipeline'] }) {
  const stages: Array<{ label: string; count: number; to: string; urgent?: boolean }> = [
    { label: 'To accept', count: pipeline.to_accept, to: '/seller/orders', urgent: true },
    { label: 'To pack', count: pipeline.to_pack, to: '/seller/orders' },
    { label: 'To dispatch', count: pipeline.to_dispatch, to: '/seller/orders' },
    { label: 'On the road', count: pipeline.on_the_road, to: '/seller/orders' },
  ];

  return (
    <section data-testid="seller-pipeline" className="mb-4">
      {/* The only genuinely time-critical thing on the page, so it gets a line
          of its own above the counts rather than a colour inside them. */}
      {pipeline.expiring_within_5_minutes > 0 ? (
        <Link
          to="/seller/orders"
          className="mb-2.5 flex min-h-11 items-center gap-2 rounded-card border border-danger bg-danger-tint px-3.5 py-2.5 no-underline"
        >
          <Icon name="clock" size={15} strokeWidth={2} className="shrink-0 text-danger" />
          <span className="min-w-0 flex-1 text-[13px] font-semibold text-danger">
            {pipeline.expiring_within_5_minutes}{' '}
            {pipeline.expiring_within_5_minutes === 1 ? 'order goes' : 'orders go'} back to the
            buyer within 5 minutes
          </span>
          <Icon name="chevron" size={14} strokeWidth={2} className="shrink-0 text-danger" />
        </Link>
      ) : null}

      <div className="grid grid-cols-2 gap-2.5 lg:grid-cols-5">
        {stages.map((stage) => (
          <Link
            key={stage.label}
            to={stage.to}
            className={cn(
              'rounded-card border px-3.5 py-3 no-underline',
              stage.count > 0 && stage.urgent
                ? 'border-caution bg-caution-tint'
                : 'border-line bg-white',
            )}
          >
            <p
              className={cn(
                'm-0 font-display text-[24px] font-bold leading-none tracking-[-0.02em] tabular',
                stage.count > 0 && stage.urgent ? 'text-caution-ink' : 'text-ink',
              )}
            >
              {stage.count}
            </p>
            <p className="m-0 mt-1 text-[11.5px] text-muted">{stage.label}</p>
          </Link>
        ))}

        {/* Not a queue — the day's score, which is the one people want after
            a long shift. */}
        <div className="rounded-card border border-verified bg-verified-tint px-3.5 py-3">
          <p className="m-0 font-display text-[24px] font-bold leading-none tracking-[-0.02em] tabular text-verified">
            {pipeline.delivered_today}
          </p>
          <p className="m-0 mt-1 text-[11.5px] text-verified">Delivered today</p>
        </div>
      </div>
    </section>
  );
}

/** The shelf as a number, including what it is worth. */
function StockCard({ stock }: { stock: SellerDashboard['stock'] }) {
  return (
    <section className="rounded-card border border-line bg-white p-4">
      <p className="m-0 font-mono text-[10px] uppercase tracking-[0.12em] text-faint">Your shelf</p>

      <p className="m-0 mt-1 font-display text-[24px] font-bold leading-none tracking-[-0.02em] tabular">
        {stock.value_display}
      </p>
      {/* At their own price, which is the figure a pharmacist uses to judge
          whether they are over-committed — and one no screen has shown them. */}
      <p className="m-0 mt-1 text-[12px] text-muted">
        stock on sale, at your prices · {stock.listings} listings
      </p>

      <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 border-t border-line pt-3 text-[12.5px]">
        <Fact label="On sale" value={`${stock.on_sale}`} />
        <Fact label="Out of stock" value={`${stock.out_of_stock}`} bad={stock.out_of_stock > 0} />
        <Fact label="Running low" value={`${stock.running_low}`} bad={stock.running_low > 0} />
        <Fact label="Too short-dated" value={`${stock.short_dated}`} bad={stock.short_dated > 0} />
      </dl>
    </section>
  );
}

/** Money, when it lands, and who is buying. */
function PayoutsCard({
  payouts,
  customers,
}: {
  payouts: SellerDashboard['payouts'];
  customers: SellerDashboard['customers'];
}) {
  return (
    <section className="rounded-card border border-line bg-white p-4">
      <p className="m-0 font-mono text-[10px] uppercase tracking-[0.12em] text-faint">
        Payouts and customers
      </p>

      <dl className="mt-2 space-y-2 text-[12.5px]">
        <Fact label="Settled to your bank" value={payouts.settled_display} />
        <Fact
          label="Next settlement"
          value={payouts.next_settlement_at ? dayOf(payouts.next_settlement_at) : '—'}
        />
        {/* Usually the surprising one: a refund being recovered from future
            orders, capped so it never starves working capital (§4.11). */}
        <Fact
          label="Being recovered"
          value={payouts.owed_display}
          bad={payouts.owed_kobo > 0}
        />
      </dl>

      <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 border-t border-line pt-3 text-[12.5px]">
        <Fact label="Buyers" value={`${customers.buyers}`} />
        <Fact
          label="Came back"
          value={
            customers.repeat_rate_bps === null
              ? '—'
              : `${Math.round(customers.repeat_rate_bps / 100)}%`
          }
        />
      </dl>
    </section>
  );
}

/**
 * When the work arrives.
 *
 * A pharmacy staffs a counter by the hour, and no total can make a rota
 * decision for them. Bars rather than a line: these are twenty-four separate
 * buckets, not a series moving through time, and joining them with a curve
 * would imply a continuity that is not there.
 */
function RhythmCard({ busiest }: { busiest: SellerDashboard['busiest'] }) {
  const hours = busiest.by_hour;
  const peak = Math.max(1, ...hours.map((h) => h.value));
  const days = busiest.by_weekday;
  const peakDay = Math.max(1, ...days.map((d) => d.value));

  return (
    <section className="rounded-card border border-line bg-white p-4">
      <p className="m-0 font-mono text-[10px] uppercase tracking-[0.12em] text-faint">
        When orders arrive
      </p>

      <div className="mt-3 flex h-[64px] items-end gap-[3px]">
        {hours.map((hour) => (
          <span
            key={hour.label}
            title={`${hour.label}:00 — ${hour.value}`}
            className="min-w-0 flex-1 rounded-t-[2px] bg-blue/80"
            // A zero still shows a sliver, so twenty-four buckets read as a
            // row of hours rather than as a gap with three bars in it.
            style={{ height: `${Math.max(3, (hour.value / peak) * 100)}%` }}
          />
        ))}
      </div>
      <div className="mt-1 flex justify-between text-[10px] text-faint">
        <span>00:00</span>
        <span>12:00</span>
        <span>23:00</span>
      </div>

      <div className="mt-3 flex items-end gap-1.5 border-t border-line pt-3">
        {days.map((day) => (
          <div key={day.label} className="flex min-w-0 flex-1 flex-col items-center gap-1">
            <span className="flex h-[34px] w-full items-end">
              <span
                className="w-full rounded-t-[2px] bg-blue-tint-2"
                style={{ height: `${Math.max(4, (day.value / peakDay) * 100)}%` }}
              />
            </span>
            <span className="text-[10px] text-faint">{day.label}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

/**
 * What has happened lately, with the date it happened on.
 *
 * Read from the order events — the audit trail the money layer writes — so it
 * cannot drift from what actually occurred.
 */
function ActivityCard({ activity }: { activity: SellerDashboard['activity'] }) {
  return (
    <section className="rounded-card border border-line bg-white p-4">
      <p className="m-0 mb-2 font-mono text-[10px] uppercase tracking-[0.12em] text-faint">
        Recent activity
      </p>

      {activity.length === 0 ? (
        <p className="m-0 text-[13px] text-muted">
          Nothing yet. Orders, packs and deliveries appear here as they happen.
        </p>
      ) : (
        <ol className="m-0 list-none space-y-2 p-0">
          {activity.map((event, index) => (
            <li
              key={`${event.reference}-${event.at}-${index}`}
              className="flex items-baseline gap-2 text-[12.5px]"
            >
              <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-faint">
                {shortDate(event.at)}
              </span>
              <span className="min-w-0 flex-1 truncate text-ink">{said(event.type)}</span>
              <span className="shrink-0 font-mono text-[11px] text-faint">{event.reference}</span>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

/** Event types are a contract; these are the words for them. */
function said(type: string): string {
  switch (type) {
    case 'order.placed':
      return 'Order placed';
    case 'order.accepted':
      return 'You accepted it';
    case 'order.packed':
      return 'Packed';
    case 'order.dispatched':
      return 'Dispatched';
    case 'order.delivered':
      return 'Delivered';
    case 'order.cancelled':
      return 'Cancelled';
    default:
      return type;
  }
}

function shortDate(iso: string | null): string {
  if (!iso) return '';

  return new Date(iso).toLocaleDateString('en-NG', { day: 'numeric', month: 'short' });
}

function dayOf(iso: string): string {
  return new Date(iso).toLocaleDateString('en-NG', { day: 'numeric', month: 'short' });
}

/**
 * The three things that cost money by sitting there.
 *
 * Above the charts, because a listing that is live and cannot dispense is
 * losing orders right now, and a sparkline of that fact would be a way of not
 * saying so.
 */
function Attention({ data }: { data: SellerDashboard }) {
  // `satisfies` rather than an annotation: the literals keep their narrow
  // types so a typo in an icon name is caught here, which an annotation on the
  // array would widen away.
  const rows = ([
    {
      icon: 'orders',
      label: 'waiting for you to accept',
      count: data.attention.awaiting_accept,
      to: '/seller',
      bad: true,
    },
    {
      icon: 'box',
      label: 'listings on sale with no stock',
      count: data.attention.out_of_stock,
      to: '/seller/listings',
    },
    {
      icon: 'clock',
      label: 'listings too short-dated to sell',
      count: data.attention.short_dated,
      to: '/seller/listings',
    },
  ] satisfies Array<{
    icon: IconName;
    label: string;
    count: number;
    to: string;
    bad?: boolean;
  }>).filter((row) => row.count > 0);

  if (rows.length === 0) return null;

  return (
    <section
      data-testid="seller-attention"
      className="mb-4 overflow-hidden rounded-card border border-caution bg-caution-tint"
    >
      {rows.map((row) => (
        <Link
          key={row.label}
          to={row.to}
          className="flex min-h-11 items-center gap-2.5 border-b border-caution/20 px-4 py-2.5 no-underline last:border-b-0"
        >
          <Icon name={row.icon} size={15} strokeWidth={1.9} className="shrink-0 text-caution-ink" />
          <span className="min-w-0 flex-1 text-[13px] text-caution-ink">
            <b className="font-bold">{row.count}</b> {row.label}
          </span>
          <Icon name="chevron" size={14} strokeWidth={2} className="shrink-0 text-caution-ink" />
        </Link>
      ))}
    </section>
  );
}

function Change({ bps, was }: { bps: number | null; was: string }) {
  // Null means the previous period was empty. Every number is an infinite
  // increase on nothing, and "+∞%" is noise rather than news.
  if (bps === null) {
    return <p className="m-0 mt-1.5 text-[12px] text-faint">No earlier period to compare with</p>;
  }

  const up = bps >= 0;

  return (
    <p className="m-0 mt-1.5 flex items-center gap-1.5 text-[12px]">
      <span
        className={cn(
          'inline-flex items-center gap-0.5 rounded-pill px-1.5 py-[1px] font-bold',
          up ? 'bg-verified-tint text-verified' : 'bg-danger-tint text-danger',
        )}
      >
        <Icon name={up ? 'up' : 'down'} size={11} strokeWidth={2.4} />
        {Math.abs(Math.round(bps / 100))}%
      </span>
      <span className="text-faint">was {was}</span>
    </p>
  );
}

function Fact({ label, value, bad = false }: { label: string; value: string; bad?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <dt className="text-muted">{label}</dt>
      <dd
        className={cn(
          'm-0 font-mono font-bold tabular',
          bad ? 'text-caution-ink' : 'text-ink',
        )}
      >
        {value}
      </dd>
    </div>
  );
}

/**
 * Kobo as a short naira string, for a chart readout only.
 *
 * NOT for any figure somebody compares against a statement — those arrive
 * pre-formatted from the server (§9.10) and are printed as sent. This exists
 * because "₦1,245,000" does not fit under a crosshair on a phone.
 */
function nairaish(kobo: number): string {
  const naira = Math.round(kobo / 100);

  if (naira >= 1_000_000) return `₦${(naira / 1_000_000).toFixed(1)}m`;
  if (naira >= 1_000) return `₦${Math.round(naira / 1_000)}k`;

  return `₦${naira}`;
}

function Loading() {
  return (
    <div className="space-y-3">
      <Skeleton className="h-[230px] w-full" radius="card" />
      <div className="grid gap-3 md:grid-cols-2">
        <Skeleton className="h-[200px] w-full" radius="card" />
        <Skeleton className="h-[200px] w-full" radius="card" />
      </div>
      <Skeleton className="h-[180px] w-full" radius="card" />
    </div>
  );
}
